<template>
  <v-card flat>
    <v-card-title class="px-0">
      <v-icon v-if="error" color="error" class="mr-2">mdi-close-circle</v-icon>
      Scan Gauge Kit
    </v-card-title>
    <v-fade-transition>
      <div class="error--text pb-5" v-if="error">
        {{ this.error }}
      </div>
    </v-fade-transition>
    <v-row>
      <v-row v-if="initialising" class="my-5">
        <v-col class="text-center">
          <LoadingSpinner />
        </v-col>
      </v-row>
      <v-col
        cols="12"
        class="text-center"
        v-if="!initialising && scanGaugeKits?.length === 0"
      >
        You currently have no scan gauge kits associated to your account.
      </v-col>
      <v-col cols="2" v-for="(kit, index) in scanGaugeKits || []" :key="index">
        <v-card
          flat
          class="text-center rounded-10 mx-auto"
          :class="{ 'btn-primary': kit.kit_box_id === scanGaugeKit }"
          @click="scanGaugeKit = kit.kit_box_id"
        >
          <v-img contain :src="require('@/assets/sgk-gen2.png')" />
          <small>
            {{ prettyKitName(kit.kit_box_id) }}
          </small>
        </v-card>
      </v-col>
      <v-col
        cols="2"
        v-if="!initialising && isOwner && (kitQuotasRemaining || upgradeable)"
      >
        <v-btn
          width="100%"
          min-height="142px"
          height="100%"
          class="rounded-10 elevation-0"
          style="text-transform: none"
          @click="handleOrder"
        >
          <template v-slot:default>
            <div class="d-flex flex-column" style="opacity: 0.6">
              <v-icon size="36" class="mb-2">mdi-plus</v-icon>
              <span class="text-body-2">Purchase</span>
            </div>
          </template>
        </v-btn>
      </v-col>
    </v-row>
    <PurchaseDialog
      v-model="dialog"
      :item="lineItem"
      :loading="loading"
      :strategy="acquisitionStrategy"
      :totalPriceCents="totalPriceCents"
      :totalTaxAmount="totalTaxAmount"
      :taxInclusive="taxInclusive"
      :customerBalance="customerBalance"
      @confirm="orderScanGaugeKit"
      @reloadAcquisitionStrategy="loadAcquisitionStrategy"
    />
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import { deepEqual } from "@/utils"
import client from "@/lib/ApiClient"
import PurchaseDialog from "@/components/shared/PurchaseDialog.vue"

export default {
  name: "ScanGaugeKitSelector",

  props: ["value", "hasGaugeScans"],

  inject: ["form"],

  components: { PurchaseDialog },

  data() {
    return {
      dialog: false,
      initialising: false,
      loading: false,
      invoice_uid: null,
      error: null,
      acquisitionStrategy: null,
      totalPriceCents: null,
      totalTaxAmount: null,
      taxInclusive: null,
      customerBalance: null
    }
  },

  watch: {
    scanGaugeKits(val, prevVal) {
      if (val && !deepEqual(val, prevVal)) {
        this.initialising = false
        this.loading = false
        if (this.dialog) this.dialog = false
      }
    }
  },

  computed: {
    ...mapGetters([
      "isOwner",
      "country",
      "currentQuotas",
      "plans",
      "currentPlan",
      "products",
      "subscription",
      "scanGaugeKits",
      "currentTenant",
      "tenantShippingAddresses"
    ]),

    scanGaugeKit: {
      get() {
        return this.value
      },

      set(value) {
        this.$emit("input", value)
      }
    },

    lineItem() {
      const product = this.products.find(
        (p) => p.product_code === "GAUGEKIT-GEN2"
      )
      let tax_amount = null
      if (this.acquisitionStrategy)
        tax_amount = this.acquisitionStrategy[0].entity_metadata?.tax?.amount
      return {
        description: "Scan Gauge Kit",
        quantity: 1,
        currency: this.currentPlan?.subscription_currency,
        price: product?.price,
        product_code: "GAUGEKIT-GEN2",
        quota_code: "registered_kits",
        tax_amount: tax_amount
      }
    },

    kitQuotasRemaining() {
      const remaining_kit_quotas = this.currentQuotas?.find(
        (q) => q.quota_code === "registered_kits"
      )?.current
      return remaining_kit_quotas
    },

    upgradeable() {
      const currentPlan = this.plans.find(
        (p) => p.code === this.subscription.plan_code
      )
      const upgradeable = this.plans.some((p) => p.tier > currentPlan.tier)
      return upgradeable
    }
  },

  methods: {
    ...mapActions(["triggerReload", "setErrorMessage"]),

    validate() {
      if (!this.hasGaugeScans) {
        return true
      }
      if (!this.scanGaugeKit) {
        this.error = "Select a Scan Gauge Kit"
      }
      return !!this.scanGaugeKit
    },

    prettyKitName(kit_box_id) {
      const parts = kit_box_id.split("_")
      return `${parts[parts.length - 1]} (${parts[parts.length - 2]})`
    },

    async handleOrder() {
      if (!this.kitQuotasRemaining)
        this.$router.push({ name: "profilePage", params: { change: true } })

      const shippingAddressUid = this.tenantShippingAddresses.find(
        (addr) => addr.uid === this.currentTenant.primary_shipping_address_uid
      ).uid
      await this.loadAcquisitionStrategy(shippingAddressUid)

      this.dialog = true
    },

    async poll() {
      if (!this.invoice_uid) return
      const invoice_paid = await client.gateway.billing.checkInvoice(
        this.invoice_uid
      )
      if (invoice_paid) {
        this.triggerReload("quotas")
        this.triggerReload("scanGaugeKits")
        // this.dialog = false
      } else setTimeout(this.poll, 500)
    },

    async orderScanGaugeKit(paymentMethodId, shippingAddressUid) {
      this.loading = true
      const payload = {
        payment_id: paymentMethodId,
        shipping_address_uid: shippingAddressUid
      }
      try {
        this.invoice_uid = await client.gateway.gaugeKits.orderScanGaugeKit(
          payload
        )
      } catch (error) {
        this.setErrorMessage(
          `Failed to order Scan Gauge Kit. ${
            error?.response?.data?.detail?.[0] || ""
          }`
        )
        this.loading = false
        return
      }
      this.poll()
    },

    async loadAcquisitionStrategy(shippingAddressUid) {
      this.loading = true
      const payload = {
        items: [
          {
            product_code: this.lineItem.product_code,
            quantity: 1
          }
        ],
        payment_type: "stripe",
        shipping_address_uid: shippingAddressUid
      }

      const strategyCheckResponse = await client.gateway.billing.checkStrategy(
        payload
      )
      this.acquisitionStrategy = strategyCheckResponse.strategy
      this.totalPriceCents = strategyCheckResponse.total_price_cents
      this.totalTaxAmount = strategyCheckResponse.total_tax_amount
      this.taxInclusive = strategyCheckResponse.tax_inclusive
      this.customerBalance = strategyCheckResponse.current_balance

      this.loading = false
    }
  },

  mounted() {
    if (this.scanGaugeKits === null) this.initialising = true
    this.triggerReload("scanGaugeKits")
  },

  created() {
    this.form.register(this)
  }
}
</script>
